import { useCallback, useId, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, PlugZap, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  ASSIGNABLE_MODULES,
  ASSIGNABLE_ROLES,
  DEFAULT_TENANT_LLM_CONFIG,
  MODULE_CATALOG,
  type AppModule,
  type AssignableRole,
  type CreateUserRequest,
  type LlmProviderName,
  type TenantLlmConfig,
  type TenantSummary,
  type UserSummary,
} from '@vce/shared';
import { PageHeader } from '@/app/layout';
import { useAuth } from '@/app/auth-context';
import { TenantFormDialog } from '@/components/tenant-form-dialog';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  QueryState,
  Select,
  Spinner,
} from '@/components/ui/primitives';
import { api, ApiError } from '@/lib/api';
import { cn, formatNumber, formatRelative } from '@/lib/utils';

const LLM_PROVIDER_OPTIONS: Array<{ value: LlmProviderName; label: string; hint: string }> = [
  { value: 'anthropic', label: 'Anthropic (Claude)', hint: 'API Claude' },
  { value: 'openai', label: 'OpenAI', hint: 'GPT oficial' },
  {
    value: 'openai-compatible',
    label: 'Outro (compatível)',
    hint: 'OpenRouter, vLLM, Ollama…',
  },
];

const LLM_PROVIDER_EXAMPLES: Record<
  LlmProviderName,
  {
    modelPlaceholder: string;
    apiKeyPlaceholder: string;
    baseUrlPlaceholder: string;
    baseUrlOptional: boolean;
  }
> = {
  anthropic: {
    modelPlaceholder: 'claude-sonnet-4-6',
    apiKeyPlaceholder: 'sk-ant-…',
    baseUrlPlaceholder: 'https://api.anthropic.com',
    baseUrlOptional: true,
  },
  openai: {
    modelPlaceholder: 'gpt-4o-mini',
    apiKeyPlaceholder: 'sk-…',
    baseUrlPlaceholder: 'https://api.openai.com/v1',
    baseUrlOptional: true,
  },
  'openai-compatible': {
    modelPlaceholder: 'google/gemini-2.5-flash',
    apiKeyPlaceholder: 'sk-…',
    baseUrlPlaceholder: 'https://openrouter.ai/api/v1',
    baseUrlOptional: false,
  },
};

type AdminTab = 'users' | 'modules' | 'accounts';

const TABS: Array<{ id: AdminTab; label: string; description: string }> = [
  {
    id: 'users',
    label: 'Usuários',
    description: 'Libere ou revogue o acesso à plataforma e defina papel e contas de cada pessoa.',
  },
  {
    id: 'modules',
    label: 'Módulos',
    description: 'Marque quem pode ver cada área. O owner tem todos os módulos automaticamente.',
  },
  {
    id: 'accounts',
    label: 'Contas',
    description:
      'Cadastre contas VTEX, credenciais e o LLM de cada tenant. Somente o owner gerencia o multi-tenancy.',
  },
];

const ROLE_LABEL: Record<string, string> = {
  viewer: 'Visualizador',
  reviewer: 'Revisor',
  admin: 'Gestor',
  owner: 'Owner',
};

const ROLE_HINT: Record<AssignableRole, string> = {
  viewer: 'Só leitura nas telas liberadas.',
  reviewer: 'Aprova sugestões e dispara ingestão, auditoria e geração.',
  admin: 'Também escreve na VTEX, faz rollback e edita o guia de estilo.',
};

function isAdminTab(value: string | null): value is AdminTab {
  return value === 'users' || value === 'modules' || value === 'accounts';
}

export function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: AdminTab = isAdminTab(tabParam) ? tabParam : 'users';
  const active = TABS.find((item) => item.id === tab) ?? TABS[0]!;

  const selectTab = useCallback(
    (next: AdminTab) => {
      setSearchParams(next === 'users' ? {} : { tab: next }, { replace: true });
    },
    [setSearchParams],
  );

  return (
    <>
      <PageHeader title="Admin" description={active.description} />
      <AdminTabs value={tab} onChange={selectTab} />
      <div
        role="tabpanel"
        id={`admin-panel-${tab}`}
        aria-labelledby={`admin-tab-${tab}`}
        className="p-4 sm:p-6"
      >
        {tab === 'users' ? <UsersTab /> : null}
        {tab === 'modules' ? <ModulesTab /> : null}
        {tab === 'accounts' ? <AccountsTab /> : null}
      </div>
    </>
  );
}

function AdminTabs({ value, onChange }: { value: AdminTab; onChange(tab: AdminTab): void }) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const index = TABS.findIndex((item) => item.id === value);
    if (index < 0) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const next = TABS[(index + delta + TABS.length) % TABS.length]!;
      onChange(next.id);
      document.getElementById(`admin-tab-${next.id}`)?.focus();
    }
    if (event.key === 'Home') {
      event.preventDefault();
      onChange(TABS[0]!.id);
      document.getElementById(`admin-tab-${TABS[0]!.id}`)?.focus();
    }
    if (event.key === 'End') {
      event.preventDefault();
      const last = TABS[TABS.length - 1]!;
      onChange(last.id);
      document.getElementById(`admin-tab-${last.id}`)?.focus();
    }
  };

  return (
    <div
      role="tablist"
      aria-label="Seções do admin"
      onKeyDown={onKeyDown}
      className="flex gap-1 overflow-x-auto border-b border-[var(--color-hairline)] px-4 sm:px-6"
    >
      {TABS.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`admin-tab-${item.id}`}
            aria-selected={selected}
            aria-controls={`admin-panel-${item.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.id)}
            className={cn(
              'shrink-0 border-b-2 px-3 py-2.5 text-sm transition',
              'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-primary)]',
              selected
                ? 'border-[var(--color-primary)] font-semibold text-[var(--color-ink)]'
                : 'border-transparent font-medium text-[var(--color-muted)] hover:text-[var(--color-ink)]',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function UsersTab() {
  const queryClient = useQueryClient();
  const { user: actor } = useAuth();
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: api.users.list });
  const tenantsQuery = useQuery({ queryKey: ['tenants'], queryFn: api.tenants.list });
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserSummary | null>(null);
  const [deactivating, setDeactivating] = useState<UserSummary | null>(null);

  const tenantsById = useMemo(() => {
    const map = new Map<string, TenantSummary>();
    for (const tenant of tenantsQuery.data ?? []) map.set(tenant.id, tenant);
    return map;
  }, [tenantsQuery.data]);

  const users = usersQuery.data ?? [];
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (item) =>
        item.name.toLowerCase().includes(needle) || item.email.toLowerCase().includes(needle),
    );
  }, [search, users]);

  const activeCount = users.filter((item) => item.isActive).length;

  const update = useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: { isActive?: boolean } }) =>
      api.users.update(userId, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(variables.body.isActive === false ? 'Usuário desativado' : 'Usuário reativado');
      setDeactivating(null);
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao atualizar usuário'),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Acesso por usuário"
          description={`${activeCount} ativos · ${users.length} no total. Ativo/inativo libera a plataforma; o papel define as ações dentro dos módulos.`}
          action={
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-3.5" aria-hidden />
              Novo usuário
            </Button>
          }
        />

        <div className="border-b border-[var(--color-hairline)] px-5 py-3">
          <label className="sr-only" htmlFor="admin-user-search">
            Buscar usuários
          </label>
          <div className="relative max-w-sm">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]"
              aria-hidden
            />
            <Input
              id="admin-user-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome ou e-mail"
              className="pl-9"
              autoComplete="off"
            />
          </div>
        </div>

        <QueryState
          isLoading={usersQuery.isLoading}
          isError={usersQuery.isError}
          onRetry={() => void usersQuery.refetch()}
        >
          {filtered.length === 0 ? (
            <EmptyState
              title={users.length === 0 ? 'Nenhum usuário' : 'Nenhum resultado'}
              description={
                users.length === 0
                  ? 'Crie o primeiro usuário para liberar acesso à equipe.'
                  : 'Tente outro nome ou e-mail.'
              }
            />
          ) : (
            <ul className="divide-y divide-[var(--color-hairline)]">
              {filtered.map((item) => {
                const locked = item.role === 'owner';
                const isSelf = item.id === actor?.id;
                return (
                  <li key={item.id} className="px-5 py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex min-w-0 gap-3">
                        <div
                          aria-hidden
                          className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-elevated)] text-xs font-semibold text-[var(--color-ink)]"
                        >
                          {initials(item.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-medium text-[var(--color-ink)]">{item.name}</p>
                            <Badge tone={item.isActive ? 'success' : 'neutral'}>
                              {item.isActive ? 'ativo' : 'inativo'}
                            </Badge>
                            <Badge tone={item.role === 'owner' ? 'brand' : 'neutral'}>
                              {ROLE_LABEL[item.role] ?? item.role}
                            </Badge>
                            {item.isTester ? <Badge tone="brand">tester</Badge> : null}
                          </div>
                          <p className="mt-0.5 truncate text-xs text-[var(--color-muted)]">
                            {item.email}
                          </p>
                          <p className="mt-2 text-xs text-[var(--color-body)]">
                            <span className="font-medium text-[var(--color-muted)]">Contas: </span>
                            {accountSummary(item, tenantsById)}
                          </p>
                          <p className="mt-1 text-xs text-[var(--color-body)]">
                            <span className="font-medium text-[var(--color-muted)]">Módulos: </span>
                            {moduleSummary(item)}
                          </p>
                          <p className="mt-1 text-[11px] text-[var(--color-muted)]">
                            Último login {formatRelative(item.lastLoginAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2 sm:pt-0.5">
                        {locked ? (
                          <p className="text-xs text-[var(--color-muted)]">Owner da plataforma</p>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              onClick={() => setEditing(item)}
                              aria-label={`Editar ${item.name}`}
                            >
                              <Pencil className="size-3.5" aria-hidden />
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant={item.isActive ? 'ghost' : 'secondary'}
                              disabled={isSelf || update.isPending}
                              onClick={() => {
                                if (item.isActive) {
                                  setDeactivating(item);
                                  return;
                                }
                                update.mutate({ userId: item.id, body: { isActive: true } });
                              }}
                              aria-label={
                                item.isActive ? `Desativar ${item.name}` : `Reativar ${item.name}`
                              }
                            >
                              {item.isActive ? 'Desativar' : 'Reativar'}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </QueryState>
      </Card>

      {creating ? (
        <UserFormDialog
          tenants={tenantsQuery.data ?? []}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      ) : null}
      {editing ? (
        <UserFormDialog
          user={editing}
          tenants={tenantsQuery.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      ) : null}
      <ConfirmDialog
        open={deactivating !== null}
        title="Desativar usuário?"
        description={
          deactivating
            ? `${deactivating.name} não conseguirá entrar na plataforma até ser reativado.`
            : ''
        }
        confirmLabel="Desativar"
        isPending={update.isPending}
        onConfirm={() => {
          if (!deactivating) return;
          update.mutate({ userId: deactivating.id, body: { isActive: false } });
        }}
        onCancel={() => setDeactivating(null)}
      />
    </div>
  );
}

function UserFormDialog({
  user,
  tenants,
  onClose,
  onSaved,
}: {
  user?: UserSummary;
  tenants: TenantSummary[];
  onClose(): void;
  onSaved(): void;
}) {
  const isEdit = user !== undefined;
  const [form, setForm] = useState<CreateUserRequest>(() => emptyUserForm(user));

  const create = useMutation({
    mutationFn: () => api.users.create(form),
    onSuccess: () => {
      toast.success('Usuário criado');
      onSaved();
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao criar usuário'),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!user) return;
      await api.users.update(user.id, {
        name: form.name,
        role: form.role,
        isTester: form.isTester,
      });
      await api.users.replaceTenants(user.id, form.tenantIds);
      await api.users.replaceModules(user.id, form.modules);
    },
    onSuccess: () => {
      toast.success('Acessos atualizados');
      onSaved();
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao salvar acessos'),
  });

  const pending = create.isPending || save.isPending;

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    if (isEdit) save.mutate();
    else create.mutate();
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={isEdit ? `Editar ${user.name}` : 'Novo usuário'}
      description={
        isEdit
          ? 'Atualize papel, contas e módulos. A senha não é alterada por aqui.'
          : 'A pessoa entra com a senha temporária. Depois disso, só acessa as contas e módulos marcados.'
      }
    >
      <UserFormFields
        form={form}
        tenants={tenants}
        showPassword={!isEdit}
        pending={pending}
        submitLabel={isEdit ? 'Salvar acessos' : 'Criar usuário'}
        onChange={setForm}
        onSubmit={handleSubmit}
        onCancel={onClose}
      />
    </Dialog>
  );
}

function UserFormFields({
  form,
  tenants,
  showPassword,
  pending,
  submitLabel,
  onChange,
  onSubmit,
  onCancel,
}: {
  form: CreateUserRequest;
  tenants: TenantSummary[];
  showPassword: boolean;
  pending: boolean;
  submitLabel: string;
  onChange(next: CreateUserRequest): void;
  onSubmit(event: FormEvent): void;
  onCancel(): void;
}) {
  const nameId = useId();
  const emailId = useId();
  const passwordId = useId();
  const roleId = useId();
  const roleHintId = `${roleId}-hint`;
  const passwordHintId = `${passwordId}-hint`;

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <div className="grid gap-3 sm:grid-cols-2">
        <LabeledField label="Nome" htmlFor={nameId}>
          <Input
            id={nameId}
            name="name"
            autoComplete="name"
            value={form.name}
            onChange={(event) => onChange({ ...form, name: event.target.value })}
            required
          />
        </LabeledField>
        <LabeledField label="E-mail" htmlFor={emailId}>
          <Input
            id={emailId}
            name="email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(event) => onChange({ ...form, email: event.target.value })}
            required
            disabled={!showPassword}
          />
        </LabeledField>
        {showPassword ? (
          <LabeledField
            label="Senha temporária"
            htmlFor={passwordId}
            hint="Mínimo de 8 caracteres. Peça para a pessoa trocar no primeiro acesso."
          >
            <Input
              id={passwordId}
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              aria-describedby={passwordHintId}
              value={form.password}
              onChange={(event) => onChange({ ...form, password: event.target.value })}
              required
            />
          </LabeledField>
        ) : null}
        <LabeledField label="Papel" htmlFor={roleId} hint={ROLE_HINT[form.role]}>
          <Select
            id={roleId}
            className="w-full"
            value={form.role}
            aria-describedby={roleHintId}
            onChange={(event) => onChange({ ...form, role: event.target.value as AssignableRole })}
          >
            {ASSIGNABLE_ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABEL[role]}
              </option>
            ))}
          </Select>
        </LabeledField>
      </div>

      <label className="flex items-start gap-2 rounded-[10px] border border-[var(--color-hairline)] px-3 py-2.5 text-sm text-[var(--color-body)]">
        <input
          type="checkbox"
          className="mt-0.5 size-4 shrink-0 accent-[var(--color-primary)]"
          checked={form.isTester}
          onChange={(event) => onChange({ ...form, isTester: event.target.checked })}
        />
        <span>
          <span className="font-medium text-[var(--color-ink)]">Tester</span>
          <span className="mt-0.5 block text-xs text-[var(--color-muted)]">
            Pode criar e gerenciar as próprias contas. Ingestão e geração limitadas a 5 produtos (1
            sugestão por produto).
          </span>
        </span>
      </label>

      <TenantChecklist
        tenants={tenants}
        selected={form.tenantIds}
        onChange={(tenantIds) => onChange({ ...form, tenantIds })}
      />
      <ModuleChecklist
        selected={form.modules}
        onChange={(modules) => onChange({ ...form, modules })}
      />

      <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={pending}>
          {pending ? 'Salvando…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function ModulesTab() {
  const queryClient = useQueryClient();
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: api.users.list });

  const replace = useMutation({
    mutationFn: ({ userId, modules }: { userId: string; modules: AppModule[] }) =>
      api.users.replaceModules(userId, modules),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao atualizar módulo'),
  });

  const assignableUsers = (usersQuery.data ?? []).filter((item) => item.role !== 'owner');

  return (
    <Card>
      <CardHeader
        title="Acesso por módulo"
        description="Cada coluna é uma área da ferramenta. Usuários inativos aparecem, mas não podem ser alterados."
      />
      <QueryState
        isLoading={usersQuery.isLoading}
        isError={usersQuery.isError}
        onRetry={() => void usersQuery.refetch()}
      >
        {assignableUsers.length === 0 ? (
          <EmptyState
            title="Nenhum usuário atribuível"
            description="Crie um usuário na aba Usuários para liberar módulos."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[44rem] border-collapse text-left text-sm">
              <caption className="sr-only">
                Matriz de acesso: usuários nas linhas, módulos nas colunas.
              </caption>
              <thead>
                <tr className="border-b border-[var(--color-hairline)] text-[11px] font-semibold uppercase tracking-[1px] text-[var(--color-muted)]">
                  <th scope="col" className="sticky left-0 bg-[var(--color-surface-card)] px-5 py-3">
                    Usuário
                  </th>
                  {ASSIGNABLE_MODULES.map((module) => {
                    const count = assignableUsers.filter((item) => item.modules.includes(module)).length;
                    return (
                      <th key={module} scope="col" className="px-2 py-3 text-center font-semibold">
                        <span className="block normal-case tracking-normal text-[var(--color-ink)]">
                          {MODULE_CATALOG[module].label}
                        </span>
                        <span className="mt-0.5 block font-medium normal-case tracking-normal">
                          {count} de {assignableUsers.length}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {assignableUsers.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-[var(--color-hairline)] last:border-b-0"
                  >
                    <th
                      scope="row"
                      className="sticky left-0 bg-[var(--color-surface-card)] px-5 py-2 text-left font-medium text-[var(--color-ink)]"
                    >
                      <span className="block truncate">{item.name}</span>
                      <span className="block truncate text-[11px] font-normal text-[var(--color-muted)]">
                        {item.email}
                        {item.isActive ? '' : ' · inativo'}
                      </span>
                    </th>
                    {ASSIGNABLE_MODULES.map((module) => {
                      const checked = item.modules.includes(module);
                      const label = `${MODULE_CATALOG[module].label} para ${item.name}`;
                      return (
                        <td key={module} className="p-0 text-center">
                          <label className="flex min-h-11 cursor-pointer items-center justify-center px-2 py-2">
                            <span className="sr-only">{label}</span>
                            <input
                              type="checkbox"
                              className="size-4 accent-[var(--color-primary)]"
                              checked={checked}
                              disabled={!item.isActive || replace.isPending}
                              onChange={(event) => {
                                const next = event.target.checked
                                  ? [...new Set([...item.modules, module])]
                                  : item.modules.filter((current) => current !== module);
                                replace.mutate({ userId: item.id, modules: next });
                              }}
                            />
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
    </Card>
  );
}

function AccountsTab() {
  const queryClient = useQueryClient();
  const tenantsQuery = useQuery({ queryKey: ['tenants'], queryFn: api.tenants.list });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TenantSummary | null>(null);
  const [llmTenant, setLlmTenant] = useState<TenantSummary | null>(null);

  const tenants = tenantsQuery.data ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Contas VTEX"
          description={`${tenants.length} cadastrada${tenants.length === 1 ? '' : 's'}. Credenciais e LLM por conta (AES-256-GCM).`}
          action={
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-3.5" aria-hidden />
              Nova conta
            </Button>
          }
        />
        <QueryState
          isLoading={tenantsQuery.isLoading}
          isError={tenantsQuery.isError}
          onRetry={() => void tenantsQuery.refetch()}
        >
          {tenants.length === 0 ? (
            <EmptyState
              title="Nenhuma conta cadastrada"
              description="Cadastre a primeira conta VTEX para começar o pipeline de enriquecimento."
              action={
                <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                  Nova conta
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-[var(--color-hairline)]">
              {tenants.map((tenant) => (
                <AccountRow
                  key={tenant.id}
                  tenant={tenant}
                  onEdit={() => setEditing(tenant)}
                  onConfigureLlm={() => setLlmTenant(tenant)}
                />
              ))}
            </ul>
          )}
        </QueryState>
      </Card>

      {creating ? (
        <TenantFormDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      ) : null}
      {editing ? (
        <TenantFormDialog
          tenant={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      ) : null}
      {llmTenant ? (
        <TenantLlmDialog tenant={llmTenant} onClose={() => setLlmTenant(null)} />
      ) : null}
    </div>
  );
}

function AccountRow({
  tenant,
  onEdit,
  onConfigureLlm,
}: {
  tenant: TenantSummary;
  onEdit(): void;
  onConfigureLlm(): void;
}) {
  const queryClient = useQueryClient();
  const test = useMutation({
    mutationFn: () => api.tenants.testConnection(tenant.id),
    onSuccess: (result) => {
      if (result.ok) {
        toast.success(
          `${result.message} · ${result.sampleProductIds.length} produtos amostrados em ${result.latencyMs}ms`,
        );
      } else {
        toast.error(result.message);
      }
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha no teste de conexão'),
  });

  return (
    <li className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <p className="text-sm font-medium text-[var(--color-ink)]">{tenant.name}</p>
        <p className="text-xs text-[var(--color-muted)]">
          {tenant.accountName}.{tenant.environment}.com.br
        </p>
        <div className="mt-2 flex flex-wrap gap-1">
          {tenant.isSandbox ? <Badge tone="brand">sandbox</Badge> : null}
          {!tenant.isActive ? <Badge tone="neutral">inativa</Badge> : null}
          {!tenant.hasCredentials ? <Badge tone="critical">sem credencial</Badge> : null}
          <Badge tone="neutral">
            {formatNumber(tenant.productCount)} produtos · ingerido{' '}
            {formatRelative(tenant.lastIngestionAt)}
          </Badge>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <Button
          size="sm"
          disabled={test.isPending}
          onClick={() => test.mutate()}
          aria-label={`Testar conexão de ${tenant.name}`}
        >
          <PlugZap className="size-3.5" aria-hidden />
          {test.isPending ? 'Testando…' : 'Testar'}
        </Button>
        <Button
          size="sm"
          onClick={onConfigureLlm}
          aria-label={`Configurar IA de ${tenant.name}`}
        >
          IA
        </Button>
        <Button size="sm" onClick={onEdit} aria-label={`Editar ${tenant.name}`}>
          <Pencil className="size-3.5" aria-hidden />
          Editar
        </Button>
      </div>
    </li>
  );
}

function TenantLlmDialog({
  tenant,
  onClose,
}: {
  tenant: TenantSummary;
  onClose(): void;
}) {
  const query = useQuery({
    queryKey: ['tenant-llm-config', tenant.id],
    queryFn: () => api.tenants.getLlmConfig(tenant.id),
  });

  return (
    <Dialog
      open
      size="lg"
      title={`IA · ${tenant.name}`}
      description="Provedor e modelo desta conta. Desligado = fallback do ambiente da plataforma."
      onClose={onClose}
    >
      {query.isLoading ? (
        <Spinner label="Carregando IA da conta…" />
      ) : query.isError ? (
        <EmptyState
          title="Não foi possível carregar a IA"
          action={
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      ) : (
        <TenantLlmForm
          tenantId={tenant.id}
          initial={query.data ?? DEFAULT_TENANT_LLM_CONFIG}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
}

function TenantLlmForm({
  tenantId,
  initial,
  onClose,
}: {
  tenantId: string;
  initial: TenantLlmConfig;
  onClose(): void;
}) {
  const queryClient = useQueryClient();
  const providerId = useId();
  const modelId = useId();
  const keyId = useId();
  const urlId = useId();
  const [enabled, setEnabled] = useState(initial.enabled);
  const [provider, setProvider] = useState<LlmProviderName>(initial.provider);
  const [baseUrl, setBaseUrl] = useState(initial.baseUrl);
  const [model, setModel] = useState(initial.model);
  const [apiKey, setApiKey] = useState('');
  const [clearApiKey, setClearApiKey] = useState(false);
  const [storedHasApiKey, setStoredHasApiKey] = useState(initial.hasApiKey);
  const [showAdvanced, setShowAdvanced] = useState(
    () => initial.provider === 'openai-compatible' || Boolean(initial.baseUrl),
  );
  const hasApiKey = storedHasApiKey && !clearApiKey;
  const examples = LLM_PROVIDER_EXAMPLES[provider];
  const providerMeta = LLM_PROVIDER_OPTIONS.find((item) => item.value === provider);

  const save = useMutation({
    mutationFn: () =>
      api.tenants.saveLlmConfig(tenantId, {
        enabled,
        provider,
        baseUrl,
        model,
        apiKey: apiKey.trim() || undefined,
        clearApiKey,
      }),
    onSuccess: (saved) => {
      toast.success(
        saved.enabled
          ? `IA da conta ativa (${saved.provider} · ${saved.model})`
          : 'Conta usa a IA padrão da plataforma',
      );
      setApiKey('');
      setClearApiKey(false);
      setStoredHasApiKey(saved.hasApiKey);
      void queryClient.invalidateQueries({ queryKey: ['tenant-llm-config', tenantId] });
      onClose();
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao salvar'),
  });

  return (
    <div className="space-y-4">
      <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-3.5 py-3">
        <input
          type="checkbox"
          className="mt-0.5 size-4 shrink-0 accent-[var(--color-primary)]"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-[var(--color-ink)]">
            Usar IA própria desta conta
          </span>
          <span className="mt-0.5 block text-[11px] leading-snug text-[var(--color-muted)]">
            Vale para todos os usuários do tenant. Desligado = env da plataforma.
          </span>
        </span>
      </label>

      {enabled ? (
        <div className="space-y-3">
          <div>
            <label htmlFor={providerId} className="mb-1 block text-xs font-medium text-[var(--color-body)]">
              Provedor
            </label>
            <p className="mb-1.5 text-[11px] text-[var(--color-muted)]">{providerMeta?.hint}</p>
            <Select
              id={providerId}
              className="w-full"
              value={provider}
              onChange={(event) => {
                const next = event.target.value as LlmProviderName;
                setProvider(next);
                if (next === 'openai-compatible') setShowAdvanced(true);
              }}
            >
              {LLM_PROVIDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor={modelId} className="mb-1 block text-xs font-medium text-[var(--color-body)]">
                Modelo
              </label>
              <Input
                id={modelId}
                value={model}
                placeholder={examples.modelPlaceholder}
                onChange={(event) => setModel(event.target.value)}
              />
            </div>
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <label htmlFor={keyId} className="text-xs font-medium text-[var(--color-body)]">
                  Chave de API
                </label>
                {hasApiKey ? <Badge tone="success">salva</Badge> : null}
                {clearApiKey ? <Badge tone="critical">será removida</Badge> : null}
              </div>
              <Input
                id={keyId}
                type="password"
                autoComplete="new-password"
                placeholder={hasApiKey ? 'Em branco mantém' : examples.apiKeyPlaceholder}
                value={apiKey}
                disabled={clearApiKey}
                onChange={(event) => setApiKey(event.target.value)}
              />
              {storedHasApiKey ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="mt-2"
                  onClick={() => {
                    setClearApiKey((current) => !current);
                    setApiKey('');
                  }}
                >
                  {clearApiKey ? 'Manter chave' : 'Remover chave'}
                </Button>
              ) : null}
            </div>
          </div>

          {examples.baseUrlOptional ? (
            <div>
              <button
                type="button"
                className="text-xs font-medium text-[var(--color-muted)] hover:text-[var(--color-ink)]"
                aria-expanded={showAdvanced}
                onClick={() => setShowAdvanced((current) => !current)}
              >
                {showAdvanced ? 'Ocultar URL base' : 'Opções avançadas (URL base)'}
              </button>
              {showAdvanced ? (
                <div className="mt-2">
                  <label htmlFor={urlId} className="mb-1 block text-xs font-medium text-[var(--color-body)]">
                    URL base
                  </label>
                  <Input
                    id={urlId}
                    value={baseUrl}
                    placeholder={examples.baseUrlPlaceholder}
                    onChange={(event) => setBaseUrl(event.target.value)}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div>
              <label htmlFor={urlId} className="mb-1 block text-xs font-medium text-[var(--color-body)]">
                URL do endpoint
              </label>
              <Input
                id={urlId}
                value={baseUrl}
                placeholder={examples.baseUrlPlaceholder}
                onChange={(event) => setBaseUrl(event.target.value)}
              />
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-[var(--color-muted)]">
          Com o interruptor desligado, a geração usa `LLM_*` do ambiente do worker.
        </p>
      )}

      <div className="flex justify-end gap-2 border-t border-[var(--color-hairline)] pt-4">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
}

function TenantChecklist({
  tenants,
  selected,
  onChange,
}: {
  tenants: TenantSummary[];
  selected: string[];
  onChange(tenantIds: string[]): void;
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-xs font-medium text-[var(--color-body)]">Contas</legend>
      {tenants.length === 0 ? (
        <p className="text-xs text-[var(--color-muted)]">
          Nenhuma conta cadastrada ainda. Cadastre em Contas e volte aqui.
        </p>
      ) : (
        <div className="max-h-40 space-y-1 overflow-y-auto rounded-[10px] border border-[var(--color-hairline)] px-3 py-2">
          {tenants.map((tenant) => {
            const checked = selected.includes(tenant.id);
            return (
              <label
                key={tenant.id}
                className="flex min-h-8 cursor-pointer items-center gap-2 text-sm text-[var(--color-body)]"
              >
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--color-primary)]"
                  checked={checked}
                  onChange={(event) => {
                    onChange(
                      event.target.checked
                        ? [...selected, tenant.id]
                        : selected.filter((id) => id !== tenant.id),
                    );
                  }}
                />
                <span>
                  {tenant.name}{' '}
                  <span className="text-[var(--color-muted)]">({tenant.accountName})</span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

function ModuleChecklist({
  selected,
  onChange,
}: {
  selected: AppModule[];
  onChange(modules: AppModule[]): void;
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-xs font-medium text-[var(--color-body)]">Módulos</legend>
      <div className="grid gap-1 rounded-[10px] border border-[var(--color-hairline)] px-3 py-2 sm:grid-cols-2">
        {ASSIGNABLE_MODULES.map((module) => {
          const checked = selected.includes(module);
          return (
            <label
              key={module}
              className="flex min-h-8 cursor-pointer items-start gap-2 text-sm text-[var(--color-body)]"
            >
              <input
                type="checkbox"
                className="mt-1 size-4 accent-[var(--color-primary)]"
                checked={checked}
                onChange={(event) => {
                  onChange(
                    event.target.checked
                      ? [...selected, module]
                      : selected.filter((item) => item !== module),
                  );
                }}
              />
              <span>
                <span className="block font-medium text-[var(--color-ink)]">
                  {MODULE_CATALOG[module].label}
                </span>
                <span className="block text-[11px] text-[var(--color-muted)]">
                  {MODULE_CATALOG[module].description}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function LabeledField({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-[var(--color-body)]">
        {label}
      </label>
      {hint ? (
        <p id={`${htmlFor}-hint`} className="mb-1.5 text-[11px] leading-snug text-[var(--color-muted)]">
          {hint}
        </p>
      ) : null}
      {children}
    </div>
  );
}

function emptyUserForm(user?: UserSummary): CreateUserRequest {
  if (!user) {
    return {
      email: '',
      name: '',
      password: '',
      role: 'viewer',
      isTester: false,
      tenantIds: [],
      modules: [],
    };
  }
  return {
    email: user.email,
    name: user.name,
    password: '********',
    role: user.role === 'owner' ? 'admin' : user.role,
    isTester: user.isTester,
    tenantIds: user.tenantIds,
    modules: user.modules,
  };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function accountSummary(user: UserSummary, tenantsById: Map<string, TenantSummary>): string {
  if (user.role === 'owner') return 'todas';
  if (user.tenantIds.length === 0) return 'nenhuma';
  return user.tenantIds
    .map((id) => tenantsById.get(id)?.name ?? id.slice(0, 8))
    .join(', ');
}

function moduleSummary(user: UserSummary): string {
  if (user.role === 'owner') return 'todos';
  if (user.modules.length === 0) return 'nenhum';
  return user.modules.map((module) => MODULE_CATALOG[module].label).join(', ');
}
